'use client';

import { useCallback, useEffect } from 'react';
import { getUserSpaces, switchUserSpace } from '@/app/apis';
import { useStore } from '@/app/providers/store';
import {
  buildTeamSpaces,
  findTeamSpaceById,
  isPersonalSpaceId,
  PERSONAL_TEAM_SPACE,
  PERSONAL_TEAM_SPACE_ID,
} from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { useCredits } from './use-credits';
import { useFetch } from './use-fetch';
import { useSubscription } from './use-subscription';

/** 工作区列表与切换逻辑，供 header 的独立切换器与侧边栏用户菜单共用 */
export const useTeamSpaces = () => {
  const router = useRouter();
  const { updateCredits } = useCredits();
  const { updateSubscription } = useSubscription();

  const {
    teamSpaceInfo,
    teamSpaces,
    userInfo,
    isUserInfoInitialized,
    updateUserInfo,
    updateTeamInfo,
    updateTeamSpaces,
    resetTaskFormStates,
    resetToolFormStates,
  } = useStore((state) => ({
    teamSpaceInfo: state.teamSpaceInfo,
    teamSpaces: state.teamSpaces,
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    updateUserInfo: state.updateUserInfo,
    updateTeamInfo: state.updateTeamInfo,
    updateTeamSpaces: state.updateTeamSpaces,
    resetTaskFormStates: state.resetTaskFormStates,
    resetToolFormStates: state.resetToolFormStates,
  }));

  const { run: fetchUserSpaces } = useFetch(getUserSpaces, {
    manual: true,
    onSuccess: ({ data }) => {
      updateTeamSpaces(buildTeamSpaces(data?.spaces));
    },
  });

  const { run: switchSpace } = useFetch(switchUserSpace, {
    manual: true,
    onSuccess: ({ data }) => {
      if (!data?.user) {
        return;
      }

      updateUserInfo(data.user);

      if (isPersonalSpaceId(data.user.internal?.space_id)) {
        updateTeamInfo(null);
      } else {
        updateTeamInfo(findTeamSpaceById(teamSpaces, data.user.internal?.space_id));
      }

      resetTaskFormStates();
      resetToolFormStates();
      updateCredits();
      updateSubscription();
      router.push('/home/recommend');
    },
  });

  useEffect(() => {
    if (!isUserInfoInitialized || !userInfo || teamSpaces.length > 0) {
      return;
    }
    fetchUserSpaces();
  }, [fetchUserSpaces, isUserInfoInitialized, teamSpaces.length, userInfo]);

  useEffect(() => {
    const currentTeamSpaceId = teamSpaceInfo?.space_id;
    if (isPersonalSpaceId(currentTeamSpaceId) || teamSpaces.length === 0) {
      return;
    }

    if (!teamSpaces.some((space) => space.space_id === currentTeamSpaceId)) {
      fetchUserSpaces();
    }
  }, [fetchUserSpaces, teamSpaceInfo, teamSpaces]);

  const currentSpaceId = teamSpaceInfo?.space_id ?? PERSONAL_TEAM_SPACE_ID;
  const currentSpace = findTeamSpaceById(teamSpaces, currentSpaceId) ?? teamSpaceInfo ?? PERSONAL_TEAM_SPACE;

  const switchToSpace = useCallback(
    (spaceId?: string) => {
      const nextSpaceId = spaceId ?? PERSONAL_TEAM_SPACE_ID;
      if (nextSpaceId === currentSpaceId) {
        return;
      }
      switchSpace(nextSpaceId);
    },
    [currentSpaceId, switchSpace],
  );

  return {
    teamSpaces,
    currentSpaceId,
    currentSpace,
    switchToSpace,
  };
};
